import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { recruitingAPI } from '../../services/recruitingApi';
import { ROLE_SKILL_SETS } from '../../utils/skillDataConfig';
import type { LucideIcon } from 'lucide-react';

// Skills type matching HexagonSkills component expectations
export interface HexagonSkillsMap {
  value: number;
  label: string;
  color: string;
  icon?: LucideIcon;
  description?: string;
}

export type SkillsMap = Record<string, HexagonSkillsMap>;

export interface AIRecommendation {
  skill: string;
  priority: string;
  current: number;
  target: number;
  reason: string;
  actionPlan: string;
  impactOnRole: string;
  timeframe: string;
}

interface SkillRecommendationsResponse {
  success: boolean;
  data?: {
    recommendations: AIRecommendation[];
  };
  message?: string;
}

interface TechnicalSkillsResponse {
  success: boolean;
  data?: {
    skills: SkillsMap;
  };
  message?: string;
}

interface CandidateContext {
  first_name?: string;
  last_name?: string;
  role_type?: string;
  seniority?: string;
  location?: string;
  main_skill?: string;
  tags?: string[];
  summary?: string;
}

interface UseSkillsAssessmentOptions {
  candidateId: number | string;
  candidate?: CandidateContext | null;
  enabled?: boolean;
}

interface UseSkillsAssessmentReturn {
  // Skills data
  candidateSkills: SkillsMap | null;
  setCandidateSkills: (skills: SkillsMap | null) => void;
  skillsVersion: number;
  isLoading: boolean;

  // Role selection
  selectedRole: string;
  setSelectedRole: (role: string) => void;
  roleSkillSets: typeof ROLE_SKILL_SETS;

  // Assessment type
  skillsAssessmentType: 'personal' | 'technical';
  setSkillsAssessmentType: (type: 'personal' | 'technical') => void;

  // Technical skills
  technicalSkills: SkillsMap | null;
  isGeneratingTechnicalSkills: boolean;
  handleGenerateTechnicalSkills: () => Promise<void>;

  // AI recommendations
  aiRecommendations: AIRecommendation[] | null;
  isGeneratingRecommendations: boolean;
  handleGenerateAIRecommendations: () => Promise<void>;

  // Skills update
  updateSkills: (skills: SkillsMap) => void;
  isUpdatingSkills: boolean;
}

/**
 * Custom hook for managing candidate skills assessment
 *
 * @example
 * const {
 *   candidateSkills,
 *   selectedRole,
 *   setSelectedRole,
 *   skillsAssessmentType,
 *   setSkillsAssessmentType,
 *   technicalSkills,
 *   handleGenerateTechnicalSkills,
 *   aiRecommendations,
 *   handleGenerateAIRecommendations,
 *   updateSkills,
 * } = useSkillsAssessment({
 *   candidateId: id,
 *   candidate,
 * });
 */
export function useSkillsAssessment({
  candidateId,
  candidate,
  enabled = true,
}: UseSkillsAssessmentOptions): UseSkillsAssessmentReturn {
  const queryClient = useQueryClient();

  const [candidateSkills, setCandidateSkills] = useState<SkillsMap | null>(null);
  const [skillsVersion, setSkillsVersion] = useState(0);
  const [selectedRole, setSelectedRole] = useState('DEVELOPER');
  const [aiRecommendations, setAiRecommendations] = useState<AIRecommendation[] | null>(null);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);
  const [skillsAssessmentType, setSkillsAssessmentType] = useState<'personal' | 'technical'>('personal');
  const [technicalSkills, setTechnicalSkills] = useState<SkillsMap | null>(null);
  const [isGeneratingTechnicalSkills, setIsGeneratingTechnicalSkills] = useState(false);

  const numericId = typeof candidateId === 'string' ? Number.parseInt(candidateId, 10) : candidateId;

  // Fetch candidate skills
  const { isLoading } = useQuery(
    ['candidate-skills', candidateId],
    () => api.recruiting.getCandidateSkills(numericId),
    {
      enabled: enabled && !!candidateId,
      onSuccess: (data) => {
        if (data.success && data.data) {
          setCandidateSkills(data.data);
          setSkillsVersion((prev) => prev + 1);
        }
      },
    }
  );

  // Update skills mutation
  const updateSkillsMutation = useMutation(
    async (skills: SkillsMap) => {
      return await api.recruiting.updateCandidateSkills(numericId, skills);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['candidate-skills', candidateId]);
        await queryClient.refetchQueries(['candidate-skills', candidateId]);
      },
      onError: (error: unknown) => {
        console.error('Error updating candidate skills:', error);
      },
    }
  );

  const updateSkills = (skills: SkillsMap) => {
    setCandidateSkills(skills);
    updateSkillsMutation.mutate(skills);
  };

  const handleGenerateAIRecommendations = async () => {
    if (!candidateSkills || Object.keys(candidateSkills).length === 0) {
      alert('Please ensure candidate has skills data before generating AI recommendations');
      return;
    }

    setIsGeneratingRecommendations(true);
    try {
      const response = (await recruitingAPI.getSkillRecommendations(numericId, selectedRole, {
        candidateName: `${candidate?.first_name} ${candidate?.last_name}`,
        currentRole: candidate?.role_type || 'Not specified',
        experienceLevel: candidate?.seniority || 'Not specified',
        location: candidate?.location || 'Not provided',
        mainSkill: candidate?.main_skill || 'Not specified',
        careerGoals: 'Career advancement and skill development',
        currentPosition: candidate?.role_type || 'Not specified',
        tags: candidate?.tags?.join(', ') || 'Not provided',
        summary: candidate?.summary || 'Not provided',
      })) as unknown as SkillRecommendationsResponse;

      if (response.success && response.data) {
        setAiRecommendations(response.data.recommendations);
      } else {
        alert('Failed to generate AI recommendations: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      alert('Error generating AI recommendations: ' + (error as Error).message);
    } finally {
      setIsGeneratingRecommendations(false);
    }
  };

  const handleGenerateTechnicalSkills = async () => {
    setIsGeneratingTechnicalSkills(true);
    try {
      const response = (await recruitingAPI.generateTechnicalSkillsAssessment(
        numericId
      )) as unknown as TechnicalSkillsResponse;

      if (response.success && response.data) {
        setTechnicalSkills(response.data.skills);
      } else {
        alert('Failed to generate technical skills assessment: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating technical skills assessment:', error);
      alert('Error generating technical skills assessment: ' + (error as Error).message);
    } finally {
      setIsGeneratingTechnicalSkills(false);
    }
  };

  // Handle assessment type change
  const handleSetSkillsAssessmentType = (type: 'personal' | 'technical') => {
    setSkillsAssessmentType(type);
    if (type === 'personal') {
      setTechnicalSkills(null);
    }
  };

  return {
    candidateSkills,
    setCandidateSkills,
    skillsVersion,
    isLoading,
    selectedRole,
    setSelectedRole,
    roleSkillSets: ROLE_SKILL_SETS,
    skillsAssessmentType,
    setSkillsAssessmentType: handleSetSkillsAssessmentType,
    technicalSkills,
    isGeneratingTechnicalSkills,
    handleGenerateTechnicalSkills,
    aiRecommendations,
    isGeneratingRecommendations,
    handleGenerateAIRecommendations,
    updateSkills,
    isUpdatingSkills: updateSkillsMutation.isLoading,
  };
}

export default useSkillsAssessment;
